import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import CareersContent from './CareersContent';
import PageSEO from './PageSEO'; // Import the SEO component

const CareersView = () => {
  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

  // Array of images for the photo widget with enhanced descriptions
  const images = [
    "/images/about/kinders-customer-first-always.jpg",
    "/images/careers/kinders-kitchen.jpg", 
    "/images/careers/kinders-kitchen2.jpg",
    "/images/careers/kinders-kitchen3.jpg",
    "/images/about/kinders-catering.jpg"
  ];

  // Enhanced benefits data with schema-friendly structure
  const benefits = [
    {
      heading: "Medical and Dental Benefits",
      body: "Comprehensive coverage to keep you feeling your best.",
      category: "Health Benefits"
    },
    {
      heading: "401(k) with Company Match",
      body: "Support for your future with matched retirement savings.",
      category: "Retirement Benefits"
    },
    {
      heading: "Career Growth Opportunities",
      body: "Learn new skills and grow within the company.",
      category: "Professional Development"
    },
    {
      heading: "Generous Employee Discounts",
      body: "Enjoy savings on the food you help serve.",
      category: "Employee Perks"
    }
  ];

  // Function to select two different random images
  const selectRandomImages = () => {
    const shuffled = [...images].sort(() => Math.random() - 0.5);
    return {
      image1: shuffled[0],
      image2: shuffled[1]
    };
  };

  // State for randomly selected images
  const [selectedImages, setSelectedImages] = useState(() => selectRandomImages());

  useEffect(() => {
    setSelectedImages(selectRandomImages());
  }, []);

  // Comprehensive SEO configuration for careers page
  const careersSEO = {
    title: "Careers at Kinder's Meats | BBQ Restaurant Jobs California",
    description: "Join the Kinder's Meats family! We're hiring passionate team members for our 7 California BBQ restaurants. Enjoy competitive benefits, career growth opportunities, medical/dental coverage, 401(k) matching, and employee discounts. Apply for restaurant jobs in Concord, Brentwood, Martinez, and more East Bay locations.",
    keywords: [
      "Kinder's Meats jobs",
      "BBQ restaurant careers", 
      "California restaurant jobs",
      "East Bay restaurant employment",
      "food service careers",
      "restaurant jobs Concord",
      "restaurant jobs Brentwood", 
      "restaurant jobs Martinez",
      "restaurant jobs Pleasant Hill",
      "restaurant jobs Walnut Creek",
      "restaurant jobs Hercules",
      "BBQ cook jobs",
      "deli counter jobs",
      "restaurant manager jobs",
      "family-owned restaurant careers",
      "restaurant benefits package",
      "restaurant 401k matching",
      "restaurant medical benefits",
      "food service training",
      "restaurant career growth",
      "part-time restaurant jobs",
      "full-time restaurant jobs",
      "California food service jobs",
      "restaurant team member jobs",
      "BBQ restaurant employment"
    ],
    canonicalUrl: "/careers",
    ogImage: "https://kindersmeats.com/images/careers/kinders-kitchen-team-og.jpg",
    customMeta: {
      'employment:employer': "Kinder's Meats Deli BBQ & Catering",
      'employment:industry': 'Food Service',
      'employment:benefits': 'Medical, Dental, 401k, Discounts',
      'employment:locations': 'California, East Bay'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "JobPosting",
          "@id": "https://kindersmeats.com/careers#jobposting",
          "title": "Restaurant Team Members - Multiple Positions",
          "description": "Join Kinder's Meats family! We're seeking passionate team members for various positions at our BBQ restaurants across California. Full-time and part-time opportunities available.",
          "hiringOrganization": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "jobLocation": [
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress", 
                "addressLocality": "Brentwood",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Concord", 
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Hercules",
                "addressRegion": "CA", 
                "addressCountry": "US"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Martinez",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pleasant Hill",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Place", 
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Walnut Creek",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            }
          ],
          "employmentType": ["FULL_TIME", "PART_TIME"],
          "jobBenefits": [
            "Medical and Dental Benefits",
            "401(k) with Company Match", 
            "Career Growth Opportunities",
            "Generous Employee Discounts",
            "Training and Development",
            "Flexible Scheduling"
          ],
          "industry": "Food Service",
          "occupationalCategory": [
            "Food Service",
            "Restaurant Operations", 
            "Customer Service",
            "Food Preparation"
          ],
          "qualifications": [
            "Passion for great food and customer service",
            "Ability to work in a fast-paced environment",
            "Team player with positive attitude",
            "Reliable and punctual"
          ],
          "responsibilities": [
            "Provide exceptional customer service",
            "Prepare and serve high-quality BBQ and deli items",
            "Maintain clean and organized work environment",
            "Work collaboratively with team members"
          ],
          "datePosted": new Date().toISOString().split('T')[0],
          "validThrough": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days from now
          "applicationContact": {
            "@type": "ContactPoint",
            "contactType": "HR",
            "availableLanguage": "English"
          }
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "alternateName": "Kinder's Meats",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject", 
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue across 7 California locations since 1946. Committed to quality food, exceptional service, and treating employees like family.",
          "foundingDate": "1946",
          "numberOfEmployees": {
            "@type": "QuantitativeValue",
            "minValue": 50,
            "maxValue": 200
          },
          "industry": "Food Service",
          "hasJobPosting": {
            "@id": "https://kindersmeats.com/careers#jobposting"
          },
          "workEnvironment": "Fast-paced, family-friendly restaurant environment",
          "benefits": [
            {
              "@type": "JobBenefit",
              "name": "Health Insurance",
              "description": "Comprehensive medical and dental coverage"
            },
            {
              "@type": "JobBenefit", 
              "name": "Retirement Plan",
              "description": "401(k) with company matching"
            },
            {
              "@type": "JobBenefit",
              "name": "Employee Discount",
              "description": "Generous discounts on food and beverages"
            },
            {
              "@type": "JobBenefit",
              "name": "Career Development",
              "description": "Training and advancement opportunities"
            }
          ],
          "award": "East Bay's favorite family-owned restaurant",
          "slogan": "Serve With Heart. Grill With Pride."
        },
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/careers#webpage",
          "url": "https://kindersmeats.com/careers",
          "name": "Careers at Kinder's Meats | BBQ Restaurant Jobs California", 
          "description": "Join the Kinder's Meats family! Restaurant jobs available across 7 California locations with competitive benefits and growth opportunities.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/careers#jobposting"
          },
          "breadcrumb": {
            "@id": "https://kindersmeats.com/careers#breadcrumb"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/careers#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2, 
              "name": "Careers",
              "item": "https://kindersmeats.com/careers"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What benefits do you offer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Full-time team members receive comprehensive medical and dental benefits, 401(k) with company match, career growth opportunities, and generous employee discounts."
              }
            },
            {
              "@type": "Question", 
              "name": "Do you offer part-time positions?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we offer both full-time and part-time positions at all of our 7 California locations."
              }
            },
            {
              "@type": "Question",
              "name": "What locations are you hiring for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We're hiring at all our locations: Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and both Walnut Creek locations."
              }
            },
            {
              "@type": "Question",
              "name": "What makes Kinder's a great place to work?",
              "acceptedAnswer": {
                "@type": "Answer", 
                "text": "Kinder's is a family-owned business where you're part of the family, not just the crew. We offer competitive benefits, career growth, and the chance to serve great food in a positive environment."
              }
            }
          ]
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...careersSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light section m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />
            
            {/* Careers Content */}
            <article itemScope itemType="https://schema.org/JobPosting">
              <CareersContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                title="careers at Kinder's"
                subtitle="Serve With Heart. Grill With Pride."
                image1={selectedImages.image1}
                image2={selectedImages.image2}
                image1Alt="Kinder's kitchen team preparing authentic BBQ - Join our family-oriented restaurant team"
                image2Alt="Kinder's restaurant interior showing modern kitchen and food preparation areas"
                sectionHeading="Find Your Perfect Role"
                sectionBody="At Kinder's, you are not just part of the crew. You are part of the flavor. Join a team where great food and genuine service come together to make someone's day every day."
                benefitsHeading="Perks for Full-Time Team Members"
                benefits={benefits}
                tableHeading="Current open positions"
                colorMode="colorBlack"
              />
            </article>

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Frequently Asked Questions</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What benefits do you offer?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Full-time team members receive comprehensive medical and dental benefits, 401(k) with company match, career growth opportunities, and generous employee discounts.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Do you offer part-time positions?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, we offer both full-time and part-time positions at all of our 7 California locations.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What locations are you hiring for?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We're hiring at all our locations: Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and both Walnut Creek locations.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What makes Kinder's a great place to work?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Kinder's is a family-owned business where you're part of the family, not just the crew. We offer competitive benefits, career growth, and the chance to serve great food in a positive environment.
                  </div>
                </div>
              </div>
            </section>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default CareersView;